import { get_turn_wgs_line } from "@/libs/turn-geometry";
import { getArrowsAlongLine } from "@/libs/arrows-along-line";
import { trafficCountsLayersConfig } from "../configs";

export class FillController {
  constructor(parent, BaseController) {
    this.parent = parent;
    this.mapgl = parent.mapgl;
    this.$store = parent.$store;
    this.BaseController = BaseController;
    this.currentGeom = null;

    this.ids = null;
    this.cpMouseMoveHandler = this.cpMouseMoveHandler.bind(this);
    this.cpMouseLeaveHandler = this.cpMouseLeaveHandler.bind(this);
    this.cpMouseClickHandler = this.cpMouseClickHandler.bind(this);
  }

  // toggling mode
  toggleOn() {
    const { ids } = this.$store.state.trafficCount.model;
    const cpId = ids["count_points"];
    this.ids = ids;

    ["general_count_points", "count_points"].forEach(type =>
      this.BaseController.toggleOnBaselayer(type, ids)
    );

    this.mapgl.on("mousemove", cpId, this.cpMouseMoveHandler);
    this.mapgl.on("mouseleave", cpId, this.cpMouseLeaveHandler);
    this.mapgl.on("click", this.cpMouseClickHandler);
  }
  toggleOff() {
    const { ids } = this.$store.state.trafficCount.model;
    const cpId = ids["count_points"];
    this.ids = null;

    ["general_count_points", "count_points"].forEach(type =>
      this.BaseController.toggleOffBaselayer(type, ids)
    );

    this.mapgl.on("mousemove", cpId, this.cpMouseMoveHandler);
    this.mapgl.on("mouseleave", cpId, this.cpMouseLeaveHandler);
    this.mapgl.on("click", this.cpMouseClickHandler);

    this.removeTurnsMapData();
    this.removeCommonMapData();
    this.$store.commit("CLEAR_TC_FILL");
  }

  // event handlers
  cpMouseMoveHandler() {
    this.mapgl.getCanvas().style.cursor = "pointer";
  }
  cpMouseLeaveHandler() {
    this.mapgl.getCanvas().style.cursor = "";
  }
  async cpMouseClickHandler(e) {
    const { ids } = this.$store.state.trafficCount.model;
    const cpId = ids["count_points"];
    if (!this.mapgl.getLayer(cpId)) return;

    const { x, y } = e.point;
    const bbox = [[x - 5, y - 5], [x + 5, y + 5]];
    const features = this.mapgl.queryRenderedFeatures(bbox, {
      layers: [cpId]
    });

    if (!features.length) {
      this.currentGeom = null;
      this.removeTurnsMapData();
      this.removeCommonMapData();
      this.$store.commit("CLEAR_TC_FILL");
      this.mapgl.setPaintProperty(cpId, "circle-color", "#e56566");
      return;
    }

    const feature = features[0];
    let general_count_point;

    try {
      general_count_point = JSON.parse(feature.properties.general_count_point);
    } catch (error) {
      console.log(error);
    }

    if (general_count_point === "null") return;

    this.setActiveCp(feature.properties, feature.geometry, general_count_point);
  }

  // set active cp
  async setActiveCp(feature, cpGeometry, gcp) {
    const { ids } = this.$store.state.trafficCount.model;
    const cpId = ids["count_points"];
    const { id, name } = feature;
    const { detalization_type, interval_type, geom } = gcp;
    const link = JSON.parse(feature.link) || gcp.link;

    this.currentGeom = cpGeometry;
    this.$store.commit("SET_TC_FILL", {
      activeCp: id,
      activeCpName: name,
      gcpLinkGeom: link ? link.geom : null,
      detalization_type,
      interval_type
    });

    this.mapgl.setPaintProperty(cpId, "circle-color", [
      "case",
      ["==", ["get", "id"], id],
      "#55b77e",
      "#e56566"
    ]);

    this.$store.commit("SET_TC_FILL_PROP", {
      name: "loading",
      value: true
    });

    await this.getMeasuresDates(id);
    await this.getMeasuresIntervals(id);
    await this.getMeasuresData(id);

    this.mapgl.flyTo({
      center: geom.coordinates,
      zoom: 18,
      speed: 2
    });
    this.$store.commit("SET_TC_FILL_PROP", {
      name: "loading",
      value: false
    });
  }

  // get data requests
  async getMeasuresDates(cpId) {
    try {
      const id = this.ids["count_points"];
      const url = `cp/${id}?count_point_id=${cpId}&format=dates`;
      const { data } = await this.$store.dispatch("REQUEST", { url });
      this.$store.commit("SET_TC_FILL", { dates: data });
    } catch (error) {
      console.log(error);
    }
  }
  async getMeasuresIntervals(cpId) {
    try {
      const id = this.ids["count_points"];
      const { date } = this.$store.state.trafficCount.fill;
      const url = `cp/${id}?count_point_id=${cpId}&format=time_intervals&measure_date=${date}`;
      const { data } = await this.$store.dispatch("REQUEST", { url });
      this.$store.commit("SET_TC_FILL", { intervals: data });
    } catch (error) {
      console.log(error);
    }
  }
  async getMeasuresData(cpId) {
    try {
      const id = this.ids["count_points"];
      const {
        date,
        interval,
        profile,
        coefsEnabled
      } = this.$store.state.trafficCount.fill;
      const profileId = coefsEnabled ? profile : "";
      const url = `cp/${id}?count_point_id=${cpId}&format=turn_data&measure_date=${date}&measure_interval_id=${interval}&profile_id=${profileId}`;
      const { data } = await this.$store.dispatch("REQUEST", { url });
      const { measures, transport_systems } = data;
      this.setDataOnMap(measures);
      this.setTableData(measures, transport_systems);
    } catch (error) {
      console.log(error);
    }
  }

  // set table data
  setTableData(measures, transport_systems) {
    const { detalization_type } = this.$store.state.trafficCount.fill;
    const sortedSystems = [...transport_systems.map(s => s.code).sort()];
    const titles = detalization_type === 2 ? ["name", "turn_name"] : ["name"];
    const columns = [...titles, ...sortedSystems];
    let rows;

    if (detalization_type === 2) {
      rows = measures.map(m => {
        const row = {
          row_id: m.turn_id,
          name: m.to_street,
          turn_name: m.turn_name
        };
        m.values.forEach((v, i) => (row[transport_systems[i].code] = v));

        return row;
      });
    } else {
      const { direct_values, rev_values } = measures[0];
      const direct = { row_id: "direct", name: "В прямом направлении" };
      const rev = { row_id: "rev", name: "В обратном направлении" };
      direct_values.forEach((v, i) => (direct[transport_systems[i].code] = v));
      rev_values.forEach((v, i) => (rev[transport_systems[i].code] = v));

      rows = [direct, rev];
    }

    this.$store.commit("SET_TC_FILL", { columns, rows, transport_systems });
  }

  // set data on map
  setDataOnMap(measures) {
    const { detalization_type } = this.$store.state.trafficCount.fill;

    if (detalization_type === 2) {
      this.setTurnsMapData(measures);
    } else if (detalization_type === 1) {
      this.setCommonMapData(measures);
    }
  }
  // set / remove turns map data helpers
  setTurnsMapData(measures) {
    const layer = this.mapgl.getLayer("traffic_counts_turns");
    const source = this.mapgl.getSource("traffic_counts_turns");
    const data = this.getTurnsData(measures);

    if (!layer) {
      this.mapgl.addSource("traffic_counts_turns", { type: "geojson", data });
      this.mapgl.addLayer(
        {
          id: "traffic_counts_turns",
          source: "traffic_counts_turns",
          ...trafficCountsLayersConfig["turns"]
        },
        this.ids.count_points
      );
      this.mapgl.addLayer({
        id: "traffic_counts_turns_arrow_label",
        source: "traffic_counts_turns",
        ...trafficCountsLayersConfig["turns_arrow_label"]
      });
      this.mapgl.addLayer({
        id: "traffic_counts_turns_text",
        source: "traffic_counts_turns",
        ...trafficCountsLayersConfig["turns_text"]
      });
    } else {
      source.setData(data);
    }
  }
  removeTurnsMapData() {
    if (this.mapgl.getLayer("traffic_counts_turns")) {
      this.mapgl.removeLayer("traffic_counts_turns");
      this.mapgl.removeLayer("traffic_counts_turns_arrow_label");
      this.mapgl.removeLayer("traffic_counts_turns_text");
      this.mapgl.removeSource("traffic_counts_turns");
    }
  }
  selectTurn() {
    const {
      hoveredTurn,
      detalization_type
    } = this.$store.state.trafficCount.fill;

    if (detalization_type === 2) {
      this.mapgl.setPaintProperty("traffic_counts_turns", "line-color", [
        "case",
        ["==", ["get", "id"], hoveredTurn],
        "#55b77e",
        "rgb(255, 68, 140)"
      ]);
    } else {
      this.mapgl.setPaintProperty(
        "direct_arrow",
        "line-color",
        "rgb(255, 68, 140)"
      );
      this.mapgl.setPaintProperty(
        "direct_arrow_label",
        "text-color",
        "rgb(255, 68, 140)"
      );
      this.mapgl.setPaintProperty(
        "reverse_arrow",
        "line-color",
        "rgb(255, 68, 140)"
      );
      this.mapgl.setPaintProperty(
        "reverse_arrow_label",
        "text-color",
        "rgb(255, 68, 140)"
      );

      if (hoveredTurn === "direct") {
        this.mapgl.setPaintProperty("direct_arrow", "line-color", "#55b77e");
        this.mapgl.setPaintProperty(
          "direct_arrow_label",
          "text-color",
          "#55b77e"
        );
      } else if (hoveredTurn === "rev") {
        this.mapgl.setPaintProperty("reverse_arrow", "line-color", "#55b77e");
        this.mapgl.setPaintProperty(
          "reverse_arrow_label",
          "text-color",
          "#55b77e"
        );
      }
    }
  }
  // set / remove common map data
  setCommonMapData(measures) {
    const { date, interval, gcpLinkGeom } = this.$store.state.trafficCount.fill;
    const isDateSelected = !!date && !!interval;
    const { direct_values, rev_values } = measures[0];
    const direct_total = direct_values.reduce((prev, el) => prev + el, 0);
    const rev_total = rev_values.reduce((prev, el) => prev + el, 0);

    // get source data
    const { direct, reverse } = getArrowsAlongLine(
      gcpLinkGeom.coordinates,
      this.currentGeom.coordinates,
      15,
      10
    );
    const data = {
      type: "FeatureCollection",
      features: [
        {
          ...direct,
          properties: {
            ...direct.properties,
            total: isDateSelected ? direct_total : ""
          }
        },
        {
          ...reverse,
          properties: {
            ...reverse.properties,
            total: isDateSelected ? rev_total : ""
          }
        }
      ]
    };
    const source = this.mapgl.getSource("traffic_counts_cp");

    if (!source) {
      this.mapgl.addSource("traffic_counts_cp", {
        type: "geojson",
        data
      });

      // add arrows layers
      this.mapgl.addLayer({
        id: "direct_arrow",
        source: "traffic_counts_cp",
        ...trafficCountsLayersConfig["direct_arrow"],
        filter: ["==", "type", "direct"]
      });
      this.mapgl.addLayer({
        id: "reverse_arrow",
        source: "traffic_counts_cp",
        ...trafficCountsLayersConfig["reverse_arrow"],
        filter: ["==", "type", "reverse"]
      });
      this.mapgl.addLayer({
        id: "traffic_counts_turns_arrow_label",
        source: "traffic_counts_cp",
        ...trafficCountsLayersConfig["turns_arrow_label"]
      });

      // add labels layers
      this.mapgl.addLayer({
        id: "direct_arrow_label",
        source: "traffic_counts_cp",
        ...trafficCountsLayersConfig["direct_arrow_label"],
        filter: ["==", "type", "direct"]
      });
      this.mapgl.addLayer({
        id: "reverse_arrow_label",
        source: "traffic_counts_cp",
        ...trafficCountsLayersConfig["reverse_arrow_label"],
        filter: ["==", "type", "reverse"]
      });
    } else source.setData(data);
  }
  removeCommonMapData() {
    if (this.mapgl.getLayer("direct_arrow")) {
      this.mapgl.removeLayer("traffic_counts_turns_arrow_label");
      this.mapgl.removeLayer("direct_arrow");
      this.mapgl.removeLayer("direct_arrow_label");
      this.mapgl.removeLayer("reverse_arrow");
      this.mapgl.removeLayer("reverse_arrow_label");
      this.mapgl.removeSource("traffic_counts_cp");
    }
  }

  // parse data
  getTurnsData(measures) {
    const { date, interval } = this.$store.state.trafficCount.fill;
    const isDateSelected = !!date && !!interval;
    const turns = {
      type: "FeatureCollection",
      features: measures.map(turn => {
        const { from_cp, to_cp, vianode, sausage, turn_id } = turn;
        const from = from_cp.coordinates;
        const via = vianode.coordinates;
        const to = to_cp.coordinates;
        const turnFeature = get_turn_wgs_line(from, via, to, 2, 16);
        const labels = sausage.split("-");
        const label_c = isDateSelected ? labels[0] : "";
        const label_g = isDateSelected ? labels[1] : "";
        const label_b = isDateSelected ? labels[2] : "";
        const label_t1 = isDateSelected ? labels[3] : "";
        const label_t2 = isDateSelected ? labels[4] : "";

        const total = labels.reduce((prev, el) => prev + Number(el), 0);
        const label_total = isDateSelected ? `(${total})` : "";

        turnFeature.properties = {
          id: turn_id,
          ...turn,
          label_c,
          label_g,
          label_b,
          label_t1,
          label_t2,
          label_total,
          total: isDateSelected ? total : 0
        };

        return { ...turnFeature, id: turn_id };
      })
    };

    return turns;
  }
}
